import { useEffect, useRef, useState } from "react";
import { RoadEngine } from "@/game/space/roadEngine";
import { RacerEngine } from "@/game/racer/racerEngine";
import { AsylumEngine } from "@/game/walk/asylumEngine";

// Records the in-app RoadEngine canvas to a video Blob via canvas.captureStream
// + MediaRecorder. Chrome/Android produce webm; Safari can do mp4 — the backend
// transcodes everything to a real .mp4 afterwards. Runs on autopilot for `durationMs`.
function pickMime() {
  if (typeof MediaRecorder === "undefined") return "";
  const cands = ['video/mp4;codecs="avc1.42E01E"', "video/mp4", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
  return cands.find((m) => { try { return MediaRecorder.isTypeSupported(m); } catch { return false; } }) || "";
}

export default function GameDemoRecorder({ sprites, macro = "forward", strafe, speed, spawnRule, facingCamera, backdrop, bgFull = false, barrelStyle = "wood", gameType = "SHOOTER", racing = {}, walk = {}, durationMs = 6000, onDone, onError }) {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState("countdown"); // countdown | recording | encoding
  const [count, setCount] = useState(3);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let engine = null; let recorder = null; let stream = null;
    let chunks = []; let done = false; let cd = null; let hardStop = null;

    const finish = () => {
      if (done) return; done = true;
      clearTimeout(hardStop);
      try {
        if (recorder && recorder.state !== "inactive") recorder.stop();
        else { engine?.unmount(); onDone?.(new Blob(chunks, { type: "video/webm" }), null); }
      } catch (e) { onError?.(e); }
    };

    const startRecording = () => {
      setPhase("recording");
      try {
        if (gameType === "RACING") {
          engine = new RacerEngine(canvas, {
            sprites: { car: sprites?.hero }, speed, autopilot: true, durationMs,
            trafficDensity: racing.trafficDensity, boostPads: racing.boostPads, rivalType: racing.rivalType,
            onFinish: finish, onGameOver: finish,
          });
        } else if (gameType === "WALKAROUND") {
          engine = new AsylumEngine(canvas, {
            sprites: sprites || {}, speed, autopilot: true, durationMs,
            pacing: walk.pacing, layout: walk.layout, danger: walk.danger,
            onFinish: finish, onGameOver: finish,
          });
        } else {
          engine = new RoadEngine(canvas, { sprites: sprites || {}, macro, strafe, speed, spawnRule, facingCamera, bgFull, barrelStyle, autopilot: true, durationMs, onFinish: finish, onGameOver: finish });
        }
        if (backdrop) engine.setBackdrop(backdrop);
        engine.mount();
        stream = canvas.captureStream(60);
        const mime = pickMime();
        recorder = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 6_000_000 } : undefined);
        recorder.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
        recorder.onstop = () => {
          setPhase("encoding");
          const type = recorder.mimeType || mime || "video/webm";
          let snap = null;
          try { snap = canvas.toDataURL("image/jpeg", 0.7); } catch { snap = null; }
          engine?.unmount();
          onDone?.(new Blob(chunks, { type }), snap);
        };
        recorder.start();
        hardStop = setTimeout(finish, durationMs + 1200);
      } catch (e) { engine?.unmount(); onError?.(e); }
    };

    let c = 3; setCount(3);
    cd = setInterval(() => { c -= 1; setCount(c); if (c <= 0) { clearInterval(cd); startRecording(); } }, 650);

    return () => {
      clearInterval(cd); clearTimeout(hardStop);
      try { if (recorder && recorder.state !== "inactive") recorder.stop(); } catch { /* noop */ }
      engine?.unmount();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[90] bg-black flex flex-col items-center justify-center gap-3" data-testid="gamedemo-recorder">
      <div className="relative w-[270px] aspect-[9/16] border-2 border-[#00F5D4] overflow-hidden shadow-[0_0_40px_rgba(0,245,212,0.4)]">
        <canvas ref={canvasRef} className="w-full h-full block bg-black" data-testid="gamedemo-canvas" />
        {phase === "countdown" && (
          <div className="absolute inset-0 grid place-items-center bg-black/70"><span className="font-mono text-6xl font-black text-[#00F5D4]">{count > 0 ? count : "GO"}</span></div>
        )}
        {phase === "recording" && (
          <span className="absolute top-2 left-2 flex items-center gap-1.5 font-mono text-[9px] font-black text-red-400"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> REC</span>
        )}
      </div>
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F5D4]" data-testid="gamedemo-recorder-status">
        {phase === "encoding" ? "Encoding…" : phase === "recording" ? "Recording gameplay…" : "Get ready…"}
      </span>
    </div>
  );
}
